import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { AdvancedSection } from "@/components/ui/advanced-section";
import { INTEGRATION_CATALOG, type IntegrationCatalogEntry } from "@ease/shared";

interface ProviderField {
  key: string;
  label: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
  helpText?: string;
}

interface ProviderModeSelection {
  strategy: "credential_prefix" | "environment_field";
  summary: string;
  liveIndicator: string;
  testIndicator: string;
}

interface BillingProvider {
  providerId: string;
  name: string;
  description: string;
  modeSelection: ProviderModeSelection;
  fields: ProviderField[];
  connectionId: string | null;
  connected: boolean;
  status: string;
  displayName: string | null;
  lastUsedAt: string | null;
  lastErrorMessage: string | null;
  failureCount: number;
}

interface IntegrationConnection {
  id: string;
  providerId: string;
  displayName: string | null;
  status: string;
  syncEnabled: boolean;
  lastUsedAt: string | null;
  lastErrorMessage: string | null;
  failureCount: number;
}

interface SidebarTemplateConfig {
  sidebarModules?: string[];
}

interface IntegrationConnectResponse {
  authUrl?: string;
  data?: {
    authUrl?: string;
  };
}

const CATALOG_CATEGORY_LABELS: Record<IntegrationCatalogEntry["category"], string> = {
  crm: "CRM",
  commerce: "E-Commerce",
  sales: "Sales",
  social: "Social",
  automation: "Automation",
  accounting: "Accounting",
};

const PROVIDER_BADGES: Record<string, string> = {
  ghl: "G",
  hubspot: "H",
  salesforce: "S",
  zoho: "Z",
  meta: "M",
  tiktok: "TT",
  linkedin: "In",
  youtube: "YT",
  google_business_profile: "GBP",
  shopify: "Shop",
  quickbooks: "QB",
  zapier: "Zap",
};

const MODULE_PROVIDER_RECOMMENDATIONS: Record<string, string[]> = {
  crm: ["ghl", "hubspot", "salesforce", "zoho"],
  pages: ["meta"],
  analytics: ["meta"],
  communities: ["meta"],
};

function getStatusLabel(provider: BillingProvider): string {
  if (provider.connected) return "Connected";
  if (provider.status === "error") return "Error";
  return "Not connected";
}

function buildConnectRequest(integration: IntegrationCatalogEntry, shopDomain?: string) {
  const connect = integration.connect;
  const body = connect?.body ?? "provider";
  const request: { path: string; init: { method: "POST"; body?: string } } = {
    path: connect?.path ?? "/api/integrations/oauth/initiate",
    init: { method: "POST" },
  };

  if (body === "provider") {
    request.init.body = JSON.stringify({
      providerId: connect?.providerId ?? integration.providerId,
      ...(shopDomain ? { shopDomain } : {}),
    });
  }

  if (body === "empty") {
    request.init.body = "{}";
  }

  return request;
}

function getAuthUrl(response: IntegrationConnectResponse, integration: IntegrationCatalogEntry): string | null {
  if (integration.connect?.authUrlPath === "data.authUrl") {
    return response.data?.authUrl ?? null;
  }

  return response.authUrl ?? null;
}

export default function IntegrationsPage() {
  const [providers, setProviders] = useState<BillingProvider[]>([]);
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [sidebarModules, setSidebarModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);
  const [submittingProviderId, setSubmittingProviderId] = useState<string | null>(null);
  const [connectingProviderId, setConnectingProviderId] = useState<string | null>(null);
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, Record<string, string>>>({});

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const [providerResponse, connectionResponse, templateResponse] = await Promise.all([
        apiFetch<{ data: BillingProvider[] }>("/v1/billing/providers"),
        apiFetch<{ data: IntegrationConnection[] }>("/api/integrations/connections"),
        apiFetch<{ value: SidebarTemplateConfig }>("/platform/config/resolve/crm/vertical_template"),
      ]);

      setProviders(providerResponse.data);
      setConnections(connectionResponse.data ?? []);
      setSidebarModules(Array.isArray(templateResponse.value?.sidebarModules) ? templateResponse.value.sidebarModules : []);
      setFormValues((current) => {
        const next = { ...current };
        for (const provider of providerResponse.data) {
          next[provider.providerId] ??= {};
        }
        return next;
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load payment providers");
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  const categories = useMemo(
    () => [...new Set(INTEGRATION_CATALOG.map((integration) => integration.category))],
    [],
  );

  const connectedProviderIds = useMemo(
    () => new Set(connections.map((connection) => connection.providerId)),
    [connections],
  );

  const recommendedProviderIds = useMemo(() => {
    const next = new Set<string>();
    for (const module of sidebarModules) {
      for (const providerId of MODULE_PROVIDER_RECOMMENDATIONS[module] ?? []) {
        if (!connectedProviderIds.has(providerId)) {
          next.add(providerId);
        }
      }
    }
    return next;
  }, [connectedProviderIds, sidebarModules]);

  const recommendedIntegrations = useMemo(
    () => INTEGRATION_CATALOG.filter((entry) => recommendedProviderIds.has(entry.providerId)),
    [recommendedProviderIds],
  );

  const browseIntegrations = useMemo(
    () => INTEGRATION_CATALOG.filter((entry) => !recommendedProviderIds.has(entry.providerId)),
    [recommendedProviderIds],
  );

  const updateField = (providerId: string, key: string, value: string) => {
    setFormValues((current) => ({
      ...current,
      [providerId]: {
        ...(current[providerId] ?? {}),
        [key]: value,
      },
    }));
  };

  const handleSave = async (providerId: string) => {
    setSubmittingProviderId(providerId);
    try {
      await apiFetch(`/v1/billing/providers/${providerId}`, {
        method: "POST",
        body: JSON.stringify({
          fields: formValues[providerId] ?? {},
        }),
      });
      await loadProviders();
      setExpandedProviderId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save payment provider");
    } finally {
      setSubmittingProviderId(null);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (!window.confirm("Disconnect this payment provider? Customer checkouts using it will stop working until it is reconfigured.")) {
      return;
    }

    setSubmittingProviderId(providerId);
    try {
      await apiFetch(`/v1/billing/providers/${providerId}`, { method: "DELETE" });
      await loadProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect payment provider");
    } finally {
      setSubmittingProviderId(null);
    }
  };

  const handleConnectIntegration = async (integration: IntegrationCatalogEntry) => {
    setConnectingProviderId(integration.id);
    try {
      const shopDomain = integration.requiresShopDomain
        ? window.prompt("Enter your Shopify store domain (for example: stero-store.myshopify.com)")?.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "")
        : undefined;

      if (integration.requiresShopDomain && !shopDomain) {
        return;
      }

      const request = buildConnectRequest(integration, shopDomain);
      const response = await apiFetch<IntegrationConnectResponse>(request.path, request.init);
      const authUrl = getAuthUrl(response, integration);
      if (!authUrl) {
        throw new Error("Integration authorization URL was not returned.");
      }
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start the integration connection.");
    } finally {
      setConnectingProviderId(null);
    }
  };

  const handleDisconnectIntegration = async (connectionId: string) => {
    if (!window.confirm("Disconnect this integration? Sync and connected features will stop until you reconnect it.")) {
      return;
    }
    setDisconnectingConnectionId(connectionId);
    try {
      await apiFetch(`/api/integrations/connections/${connectionId}`, { method: "DELETE" });
      await loadProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect the integration.");
    } finally {
      setDisconnectingConnectionId(null);
    }
  };

  const renderCatalogCard = (integration: IntegrationCatalogEntry) => {
    const connected = connections.find((connection) => connection.providerId === integration.providerId);
    const isComingSoon = integration.availability === "coming_soon";

    return (
      <div key={integration.id} className="rounded-md border border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-sm font-semibold">
              {PROVIDER_BADGES[integration.providerId] ?? integration.name.slice(0, 2)}
            </div>
            <div>
              <p className="font-medium text-foreground">{integration.name}</p>
              <p className="text-sm text-muted-foreground">{integration.description}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span>{CATALOG_CATEGORY_LABELS[integration.category]}</span>
                {connected ? (
                  <span className="text-[var(--color-success)]">{connected.status === "active" ? "Connected" : connected.status}</span>
                ) : isComingSoon ? (
                  <span>Coming soon</span>
                ) : (
                  <span>Available</span>
                )}
                {connected?.lastUsedAt ? <span>Used {formatDate(connected.lastUsedAt)}</span> : null}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {connected ? (
              <>
                <a
                  href="/settings/integrations/hub"
                  className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
                >
                  Manage
                </a>
                <button
                  onClick={() => void handleDisconnectIntegration(connected.id)}
                  disabled={disconnectingConnectionId === connected.id}
                  className="rounded-md border border-[var(--color-destructive)]/20 px-3 py-1.5 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/5 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => void handleConnectIntegration(integration)}
                disabled={isComingSoon || connectingProviderId === integration.id}
                className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isComingSoon ? "Coming soon" : connectingProviderId === integration.id ? "Connecting..." : "Connect"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Connected services</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Payment providers are configured with tenant-owned credentials. Other app integrations continue in the integrations hub.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      <section>
        <div>
          <h3 className="text-sm font-medium uppercase text-muted-foreground">Connected</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Live app integrations from the mounted control-plane connection seam.
          </p>
        </div>
        <div className="mt-4 space-y-3">
          {connections.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
              Connect your first integration from the sections below.
            </div>
          ) : (
            connections.map((connection) => {
              const integration = INTEGRATION_CATALOG.find((entry) => entry.providerId === connection.providerId);
              if (!integration) {
                return null;
              }
              return renderCatalogCard(integration);
            })
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium uppercase text-muted-foreground">Recommended</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Suggested from the mounted tenant vertical template instead of a fake generic list.
            </p>
          </div>
          <a href="/settings/integrations/hub" className="text-sm text-primary hover:underline">
            Open integrations hub
          </a>
        </div>
        <div className="mt-4 space-y-3">
          {recommendedIntegrations.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
              No extra recommendations are exposed from the current tenant template. Browse the full catalog below.
            </div>
          ) : (
            recommendedIntegrations.map((integration) => renderCatalogCard(integration))
          )}
        </div>
      </section>

      <section>
        <div>
          <h3 className="text-sm font-medium uppercase text-muted-foreground">Browse all</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The canonical provider catalog grouped by mounted category, including explicit coming-soon entries.
          </p>
        </div>
        <div className="mt-4 space-y-6">
          {categories.map((category) => {
            const integrations = browseIntegrations.filter((integration) => integration.category === category);
            if (integrations.length === 0) {
              return null;
            }
            return (
              <div key={category}>
                <h4 className="text-sm font-medium text-foreground">{CATALOG_CATEGORY_LABELS[category]}</h4>
                <div className="mt-3 space-y-3">
                  {integrations.map((integration) => renderCatalogCard(integration))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium uppercase text-muted-foreground">Payments</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Bring your own payment processor credentials. Secrets are encrypted per tenant through the platform KEK flow.
            </p>
          </div>
          <a
            href="/settings/billing"
            className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
          >
            Billing overview
          </a>
        </div>

        <div className="mt-4 space-y-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-border p-4">
                <div className="rounded-md bg-[var(--color-muted)]/50 h-5 w-48" />
                <div className="mt-3 rounded-md bg-[var(--color-muted)]/30 h-4 w-full" />
              </div>
            ))
          ) : (
            providers.map((provider) => {
              const currentValues = formValues[provider.providerId] ?? {};
              const expanded = expandedProviderId === provider.providerId;
              return (
                <div key={provider.providerId} className="rounded-lg border border-border p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="font-medium text-foreground">{provider.name}</p>
                      <p className="text-sm text-muted-foreground">{provider.description}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Mode selection: {provider.modeSelection.summary}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className={provider.connected ? "text-[var(--color-success)]" : ""}>
                          {getStatusLabel(provider)}
                        </span>
                        <span>Live: {provider.modeSelection.liveIndicator}</span>
                        <span>Test: {provider.modeSelection.testIndicator}</span>
                        {provider.displayName && <span>{provider.displayName}</span>}
                        {provider.lastUsedAt && <span>Used {formatDate(provider.lastUsedAt)}</span>}
                        {provider.failureCount > 0 && <span>{provider.failureCount} recent failure{provider.failureCount === 1 ? "" : "s"}</span>}
                      </div>
                      {provider.lastErrorMessage && (
                        <p className="mt-2 text-xs text-[var(--color-destructive)]">{provider.lastErrorMessage}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setExpandedProviderId(expanded ? null : provider.providerId)}
                        className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-accent"
                      >
                        {provider.connected ? "Rotate keys" : "Configure"}
                      </button>
                      {provider.connected && (
                        <button
                          onClick={() => void handleDisconnect(provider.providerId)}
                          disabled={submittingProviderId === provider.providerId}
                          className="rounded-md border border-[var(--color-destructive)]/20 px-4 py-2 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/5 disabled:opacity-50"
                        >
                          Disconnect
                        </button>
                      )}
                    </div>
                  </div>

                  {expanded && (
                    <AdvancedSection
                      className="mt-4"
                      title="Advanced credentials"
                      description={`Provider keys and tenant-owned secrets live here. ${provider.modeSelection.summary}`}
                    >
                      <div className="grid gap-4 md:grid-cols-2">
                        {provider.fields.map((field) => (
                          <label key={field.key} className="block text-sm">
                            <span className="mb-1 block text-muted-foreground">
                              {field.label}
                              {field.required ? " *" : ""}
                            </span>
                            <input
                              type={field.secret ? "password" : "text"}
                              value={currentValues[field.key] ?? ""}
                              placeholder={field.placeholder}
                              onChange={(event) => updateField(provider.providerId, field.key, event.target.value)}
                              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground"
                            />
                            {field.helpText ? (
                              <span className="mt-1 block text-xs text-muted-foreground">{field.helpText}</span>
                            ) : null}
                          </label>
                        ))}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => void handleSave(provider.providerId)}
                          disabled={submittingProviderId === provider.providerId}
                          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          {submittingProviderId === provider.providerId ? "Saving…" : provider.connected ? "Update credentials" : "Save credentials"}
                        </button>
                        <button
                          onClick={() => setExpandedProviderId(null)}
                          className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-accent"
                        >
                          Close
                        </button>
                      </div>
                    </AdvancedSection>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
